import React from "react";
import {
  Box, Container, Heading, Text, SimpleGrid, VStack, HStack,
  Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon,
  Icon, Badge, Button, Stat, StatLabel, StatNumber, StatHelpText,
} from "@chakra-ui/react";
import { FaCar, FaShip, FaMotorcycle, FaTruck, FaTools, FaPercent, FaClock, FaHandshake } from "react-icons/fa";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const NAVY = "#141C49";
const SAND = "#C2A878";

const FinanceServicesPersonalAsset: React.FC = () => {
  const loanTypes = [
    {
      title: "Car & Vehicle Loans",
      icon: FaCar,
      description: "New and used car finance with competitive rates and flexible terms",
      features: [
        "New vehicles from $10,000 with rates from 5.99% p.a.",
        "Used vehicles up to 10 years old at time of purchase",
        "Loan terms from 1 to 7 years",
        "Pre-approval available in 24 hours",
        "Trade-in assistance and balloon payment options",
        "No monthly account-keeping fees",
      ],
    },
    {
      title: "Marine & Boat Finance",
      icon: FaShip,
      description: "Specialist marine finance for boats, yachts, and watercraft",
      features: [
        "New and used boats, including sailboats and motorboats",
        "Personal watercraft (jet skis) and trailers",
        "Loan amounts from $10,000 to $500,000+",
        "Terms up to 7 years for marine assets",
        "Chattel mortgage options for business use",
        "Insurance and mooring cost considerations included",
      ],
    },
    {
      title: "Motorcycle & Recreational",
      icon: FaMotorcycle,
      description: "Finance for motorcycles, ATVs, caravans, and recreational vehicles",
      features: [
        "Motorcycles, scooters, and e-bikes",
        "Caravans, camper trailers, and motorhomes",
        "ATVs, UTVs, and off-road vehicles",
        "Loan amounts from $5,000",
        "Flexible repayment terms up to 7 years",
        "Seasonal payment options available",
      ],
    },
    {
      title: "Equipment & Business Assets",
      icon: FaTools,
      description: "Commercial equipment finance for business growth and efficiency",
      features: [
        "Machinery, tools, and equipment for any industry",
        "Technology and IT hardware (computers, servers, POS)",
        "Medical, dental, and professional equipment",
        "Chattel mortgage and commercial hire purchase",
        "Tax benefits for business asset depreciation",
        "Fast approval for established businesses (from 4 hours)",
      ],
    },
    {
      title: "Commercial Vehicles",
      icon: FaTruck,
      description: "Heavy vehicle and commercial transport finance solutions",
      features: [
        "Trucks, vans, and commercial utes",
        "Trailers, tippers, and specialised vehicles",
        "Construction and agricultural equipment",
        "Fleet finance packages available",
        "Balloon payments to reduce monthly costs",
        "GST and tax structuring advice included",
      ],
    },
  ];

  const stats = [
    { label: "Average Approval Time", value: "24 Hours", helpText: "For standard applications" },
    { label: "Loan Amounts", value: "$5K – $500K+", helpText: "Flexible financing" },
    { label: "Interest Rates From", value: "5.99% p.a.*", helpText: "Competitive secured rates" },
    { label: "Loan Terms", value: "1 – 7 Years", helpText: "Choose your term" },
  ];

  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/client/finance/services/personal-asset"
        fallbackTitle="Personal & Asset Finance – Cars, Boats, Equipment Loans"
        fallbackDescription="Flexible financing for vehicles, boats, motorcycles, equipment and business assets. Fast approvals, competitive rates from 5.99% p.a. Apply online today."
      />
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box bg={NAVY} color="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={6} align="center" textAlign="center">
            <Badge bg={SAND} color={NAVY} fontSize="md" px={4} py={2} borderRadius="full">
              Personal & Asset Finance
            </Badge>
            <Heading as="h1" size="2xl" fontWeight="bold">
              Finance Your Next Vehicle or Asset
            </Heading>
            <Text fontSize="xl" maxW="3xl" opacity={0.95}>
              From cars and boats to equipment and commercial vehicles — get fast approvals
              and flexible repayment terms on the assets that matter to you.
            </Text>
            <HStack spacing={4} pt={4}>
              <Button size="lg" bg={SAND} color={NAVY} _hover={{ opacity: 0.9 }}>
                Get Pre-Approved
              </Button>
              <Button size="lg" variant="outline" borderColor="white" color="white" _hover={{ bg: "whiteAlpha.200" }}>
                Calculate Repayments
              </Button>
            </HStack>
          </VStack>
        </Container>
      </Box>

      {/* Stats Section */}
      <Box py={12} bg="gray.50" borderBottom="1px solid" borderColor="gray.200">
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={8}>
            {stats.map((stat, index) => (
              <Stat key={index} textAlign="center">
                <StatLabel fontSize="sm" color="gray.600">{stat.label}</StatLabel>
                <StatNumber fontSize="3xl" color={NAVY} fontWeight="bold">{stat.value}</StatNumber>
                <StatHelpText fontSize="xs" color="gray.500">{stat.helpText}</StatHelpText>
              </Stat>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* Loan Types Accordion */}
      <Box py={20}>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading as="h2" size="xl" color="gray.800">
                Finance Solutions for Every Asset Type
              </Heading>
              <Text fontSize="lg" color="gray.600" maxW="3xl">
                Whether you're buying your dream car, upgrading business equipment, or financing a boat,
                we have specialised loan products tailored to your needs.
              </Text>
            </VStack>

            <Accordion allowMultiple w="full">
              {loanTypes.map((loanType, index) => (
                <AccordionItem
                  key={index}
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="xl"
                  mb={4}
                  bg="white"
                  shadow="sm"
                  _hover={{ shadow: "md" }}
                >
                  <AccordionButton py={8} px={8} _hover={{ bg: "gray.50" }} borderRadius="xl">
                    <HStack flex="1" textAlign="left" spacing={6}>
                      <Icon as={loanType.icon} boxSize={10} color={SAND} />
                      <VStack align="start" spacing={1}>
                        <Heading as="h3" size="md" color={NAVY}>{loanType.title}</Heading>
                        <Text color="gray.600" fontSize="sm">{loanType.description}</Text>
                      </VStack>
                    </HStack>
                    <AccordionIcon color={SAND} boxSize={6} />
                  </AccordionButton>
                  <AccordionPanel px={8} py={6} bg="gray.50" borderBottomRadius="xl">
                    <VStack align="start" spacing={3}>
                      {loanType.features.map((feature, featureIndex) => (
                        <HStack key={featureIndex} align="start">
                          <Icon as={FaHandshake} color={SAND} mt={1} />
                          <Text color="gray.700">{feature}</Text>
                        </HStack>
                      ))}
                    </VStack>
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>
          </VStack>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Box py={20} bg="gray.50">
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={12} alignItems="center">
            <VStack align="start" spacing={6}>
              <Badge bg={SAND} color={NAVY} fontSize="sm" px={3} py={1} borderRadius="full">
                Why Choose Us
              </Badge>
              <Heading as="h2" size="xl" color="gray.800">
                Fast, Flexible Asset Finance
              </Heading>
              <Text fontSize="lg" color="gray.600">
                We make it easy to finance your next asset with streamlined applications,
                quick decisions, and competitive rates that put you in control.
              </Text>
              <VStack align="start" spacing={4} w="full">
                <HStack>
                  <Icon as={FaClock} color={SAND} boxSize={6} />
                  <Text fontSize="lg" fontWeight="semibold">24-Hour Pre-Approval</Text>
                </HStack>
                <Text color="gray.600" pl={10}>
                  Most applications receive conditional approval within 24 hours with all documents submitted.
                </Text>
                <HStack>
                  <Icon as={FaPercent} color={SAND} boxSize={6} />
                  <Text fontSize="lg" fontWeight="semibold">Competitive Rates</Text>
                </HStack>
                <Text color="gray.600" pl={10}>
                  Access exclusive rates from our panel of specialist asset finance lenders.
                </Text>
                <HStack>
                  <Icon as={FaHandshake} color={SAND} boxSize={6} />
                  <Text fontSize="lg" fontWeight="semibold">Flexible Terms</Text>
                </HStack>
                <Text color="gray.600" pl={10}>
                  Choose from 1 to 7 year terms, with balloon payments and seasonal options available.
                </Text>
              </VStack>
            </VStack>

            <Box bg={NAVY} p={10} borderRadius="2xl" color="white">
              <VStack spacing={6} align="start">
                <Heading as="h3" size="lg">Ready to Finance Your Asset?</Heading>
                <Text opacity={0.9}>
                  Get started with our simple online application. Most customers receive
                  a decision within 24 hours.
                </Text>
                <VStack align="stretch" spacing={3} w="full" pt={4}>
                  <HStack>
                    <Box bg="rgba(194,168,120,0.3)" px={3} py={1} borderRadius="md" fontWeight="bold">1</Box>
                    <Text>Complete 3-minute online application</Text>
                  </HStack>
                  <HStack>
                    <Box bg="rgba(194,168,120,0.3)" px={3} py={1} borderRadius="md" fontWeight="bold">2</Box>
                    <Text>Upload ID and financial documents</Text>
                  </HStack>
                  <HStack>
                    <Box bg="rgba(194,168,120,0.3)" px={3} py={1} borderRadius="md" fontWeight="bold">3</Box>
                    <Text>Receive conditional approval in 24 hours</Text>
                  </HStack>
                  <HStack>
                    <Box bg="rgba(194,168,120,0.3)" px={3} py={1} borderRadius="md" fontWeight="bold">4</Box>
                    <Text>Finalise paperwork and collect your asset</Text>
                  </HStack>
                </VStack>
                <Button size="lg" bg={SAND} color={NAVY} w="full" _hover={{ opacity: 0.9 }} mt={4}>
                  Start Application
                </Button>
                <Text fontSize="sm" opacity={0.8} textAlign="center" w="full">
                  *Subject to credit assessment and lending criteria
                </Text>
              </VStack>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box py={20} bg={NAVY} color="white">
        <Container maxW="container.xl">
          <VStack spacing={8} textAlign="center">
            <Heading as="h2" size="xl">
              Get Your Personal Asset Finance Quote Today
            </Heading>
            <Text fontSize="lg" maxW="2xl" opacity={0.95}>
              Compare rates from our panel of specialist lenders and get pre-approved
              in as little as 24 hours. No obligation, fast decisions.
            </Text>
            <HStack spacing={4}>
              <Button size="lg" bg={SAND} color={NAVY} _hover={{ opacity: 0.9 }}>
                Apply Now
              </Button>
              <Button size="lg" variant="outline" borderColor="white" color="white" _hover={{ bg: "whiteAlpha.200" }}>
                Call Us: 1300 XXX XXX
              </Button>
            </HStack>
          </VStack>
        </Container>
      </Box>

      <FooterWithFourColumns />
    </Box>
  );
};

export default FinanceServicesPersonalAsset;
